import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { _Api, Api_form } from "../../api/_call";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  LocationList,
  SupplierList,
  VoucherList,
} from "../../common/graphql/commonlayouts";
import TableColumnModal from "../../common/modules/tableColumn";
import ConfirmationModel from "../../common/modules/confirmationmodel";
import Advancedfiltermodel from "../../common/modules/advanced-filter-model";
import LspNotesModal from "../../common/modules/LspNotesModal";
import SkuModal from "../../common/modules/SkuModal";
import { calDuedate } from "../../common/helpers/formatDate";
import { tbl_createreplenishHead } from "../../common/Tablehead";
import { error, loading, Updateloading,success } from "../../common/helpers/toastify";
import { Pagination } from "../../common/Pagination";
import ProductModal from "../../common/modules/ProductModal";
import ProductImageSliderModal from "../../common/modules/productImageslider";
import TransAndDeliverDate from "../../common/modules/TransAndDeliverDate";
import SocialModal from "../../common/modules/SocialModal/SocialModal";
import Select from "react-select";
import './createpoReplenishOrder.scss';
import EditColumnSubmenu from "components/common/modules/EditColumnSubmenu";
import ImportFilesReplenishOrder from "./ImportFilesReplenishOrder";
import PoPuSampleFormatModal from "../../common/modules/PoPuSampleFormatModal";

const STICKY_COL_COUNT = 5;
const SORT_KEYS = [
  null, null, null, "name", "sku", "metal", "stone", "size",
  "stock", "sale_order", "available", "reorder_point", "reorder_qty", "qty",
];
const SORT_ACCESSORS = {
  name: (r) => r?.name,
  sku: (r) => r?.SKU,
  metal: (r) => r?.metal_name,
  stone: (r) => r?.stone_name,
  size: (r) => r?.size_name,
  stock: (r) => Number(r?.total_stock) || 0,
  sale_order: (r) => Number(r?.SalesOrder) || 0,
  available: (r) => Number(r?.AvailableStock) || 0,
  reorder_point: (r) => Number(r?.ReorderPoint_total) || 0,
  reorder_qty: (r) => Number(r?.ReorderQuantity_total) || 0,
  qty: (r) => Number(r?.order_qty) || 0,
};
const getRowProductId = (row) =>
  row?.product_id || row?._id || row?.id || "";
const getVisibleOriginalPages = (originalTotal, hiddenPages) => {
  const pages = [];
  const total = Number(originalTotal) || 0;
  for (let i = 1; i <= total; i++) {
    if (!hiddenPages.has(i)) pages.push(i);
  }
  return pages;
};
const getNextOriginalPage = (deletedOriginal, originalTotal, hiddenPages) => {
  const visible = getVisibleOriginalPages(originalTotal, hiddenPages);
  return (
    visible.find((page) => page > deletedOriginal) ||
    [...visible].reverse().find((page) => page < deletedOriginal) ||
    null
  );
};
const extractMasterProducts = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.replenishList)) return data.replenishList;
  if (Array.isArray(data.data)) return data.data;
  return [data];
};
const normalizeReplenishProduct = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const product_id = getRowProductId(raw);
  if (!product_id) return null;
  const hasQty =
    raw.order_qty !== undefined && raw.order_qty !== null && raw.order_qty !== "";
  return {
    ...raw,
    product_id,
    order_qty: hasQty
      ? raw.order_qty
      : raw.ReorderQuantity_total ?? raw.reorder_qty ?? 1,
  };
};
const buildPoLine = (product_details, productId) => ({
  product_id: productId || getRowProductId(product_details),
  oldStock_id: "",
  // From Excel Customer_ID / POS link on import — same keys as create POS→PO.
  customer_id:
    product_details?.Customer_id ||
    product_details?.customer_id ||
    product_details?.pos?.customer_id ||
    "",
  so_no: "",
  po_QTY: parseInt(product_details["order_qty"], 10),
  price: product_details["price"],
  amount_total:
    parseInt(product_details["price"], 10) *
    parseInt(product_details["order_qty"], 10),
  sales_order_id: "",
  remark: "",
  Style: product_details["Style"],
  Metal: product_details["Metal"],
  Color: product_details["Color"],
  Size: product_details["Size"],
  Unit: product_details["Unit"],
  GrossWt: product_details["GrossWt"],
  NetWt: product_details["NetWt"],
  Stones: product_details["Stones"],
  ...(product_details?.pos ? { pos: product_details.pos } : {}),
});

// Portal: the table wrap and sticky columns would clip a normal tooltip.
const QtyFormulaInfo = ({ t }) => {
  const [coords, setCoords] = useState(null);
  const show = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setCoords({
      top: rect.bottom + 6,
      right: Math.max(window.innerWidth - rect.right, 8),
    });
  };
  return (
    <span
      className="ms-1"
      onMouseEnter={show}
      onMouseLeave={() => setCoords(null)}
      onClick={(e) => e.stopPropagation()}
    >
      <i className="fa fa-info-circle qty-formula-icon" aria-label={t("Qty formula")} />
      {coords
        ? createPortal(
            <div
              className="qty-formula-tooltip"
              style={{ top: coords.top, right: coords.right }}
            >
              <div className="qty-formula-tooltip-heading">{t("Qty Formula")}</div>
              <div className="qty-formula-tooltip-body">
                <div className="qty-formula-tooltip-title">{t("Reorder Point above 0")}</div>
                <div>{t("If Stock is below Reorder Point:")}</div>
                <div className="qty-formula-tooltip-formula">
                  {t("Qty = larger of (Sale Order + Reorder Point - Stock) and Reorder Qty")}
                </div>
                <div>{t("Otherwise Qty = 0")}</div>
                <div className="qty-formula-tooltip-title mt-2">{t("Reorder Point 0")}</div>
                <div className="qty-formula-tooltip-formula">
                  {t("Qty = larger of (Sale Order - Stock) and Reorder Qty")}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </span>
  );
};

const CreateReplenishOrder = (props) => {
  const { state } = useLocation();

  const inputRef = useRef(null);
  const productSearchBar = useRef(null);
  const [isInputVisible, setIsInputVisible] = useState(false);
  const [ShowProductSearchBar, setShowProductSearchBar] = useState(false);
  const { t } = useTranslation();
  const datePickerRef = useRef(null);
  const navigate = useNavigate();
  const base_url = process.env.REACT_APP_BASE_ASSETS_URL;
  const [aginationsState, setPaginationsState] = useState({
    totalPages: 20,
    currentPage: 1,
  });
  const [form_note, setForm_note] = useState(null);
  const [commanSelected_loc, setcommanSelected_loc] = useState(null);
  const [locationType,setLocationType]=useState("");
  const [commanSelected_sup, setcommanSelected_sup] = useState(null);
  const [commanSelected_vouc, setcommanSelected_vouc] = useState(null);
  const [masterSearchData, setMasterSearchData] = useState([]);
  const [selecteditem, setSelecteditem] = useState([]);
  const [orderType, set_orderType] = useState('Low Stock');
  const [Replenish_OrdersList, setReplenish_OrdersList] = useState([]);
  const [location,setLocation]=useState();
  const [importLocationName, setImportLocationName] = useState("");
  const [supplier, setSupplier] = useState();
  const [importSupplierName, setImportSupplierName] = useState("");
  const [excelloges, setExcelloges] = useState([])
  const [TransactionDate, setTransactionDate] = useState(new Date());
  const [DeliveryDate, setDeliveryDate] = useState();
  const [limit, setLimit] = useState(100);
  const [skipNumbers, setSkipNumbers] = useState(0);
  const [filtername, setFiltername] = useState('');
  const [getoneproduct, setOneproduct] = useState();
  const [newcolumndata, setnewcolumndata] = useState(tbl_createreplenishHead);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [importfile, setImportfile] = useState();
  const [startdate, setStartdate] = useState();
  const [ShowSubmitbutton, setShowSubmitbutton] = useState(true);
  const [enddate, setEnddate] = useState();
  const [imageid, setImageid] = useState();
  const [SelectAllStatus, setSelectAllStatus] = useState(false);
  const [oneproductindex, setoneproductindex] = useState();
  const [datelable, setDatelable] = useState('Replenish');
  const [cuppyqty, setCuppyqty] = useState()
  const [qty, setQty] = useState()
  const [showination, setShowination] = useState(false)
  const [poexportdata, setPoexportdata] = useState(false)
  const [poExportFormat, setPoExportFormat] = useState("xlsx")
  const [getqty, setGetqty] = useState();
  const [showEditColumns, setShowEditColumns] = useState(false);
  const closeEditColumns = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const secondRowOption = useRef(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef(null);
  const [skuList_saved, setSkuList_saved] = useState([]);
  const listFetchedRef = useRef(false);
  const stickyThRefs = useRef([]);
  const [stickyLefts, setStickyLefts] = useState([0, 0, 0, 0, 0]);
  const [hiddenOriginalPages, setHiddenOriginalPages] = useState(() => new Set());
  const deletedProductIdsRef = useRef(new Set());
  const excludedProductIdsRef = useRef(new Set());
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleClickOutside = (event) => {
    if (inputRef.current && !inputRef.current.contains(event.target)) {
      setIsInputVisible(false);
    }
    if (productSearchBar.current && !productSearchBar.current.contains(event.target)) {
      setShowProductSearchBar(false);
    }
    if (closeEditColumns.current && !closeEditColumns.current.contains(event.target)) {
      setShowEditColumns(false);
    }
    if (secondRowOption.current && !secondRowOption.current.contains(event.target)) {
      setIsOpen(false);
    }

  }

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getdeliverydate = async () => {
    setDeliveryDate(await calDuedate(new Date()));
  }
  useEffect(() => {
    getdeliverydate();

  }, []);
  useEffect(() => {
    const listElement = listRef.current;
    if (listElement && listElement.children[highlightedIndex]) {
      listElement.children[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);
  const readExcel = async () => {

    const handleValidation = (e) => {
      let formIsValid = true;
      if (!importfile) {
        formIsValid = false;
        error(t("Choose file!"));
      }
      return formIsValid;
    }
    let GetloadingID = loading();
    if (handleValidation()) {
      try {
        const formData = new FormData();
        formData.append('file', importfile);
        let res = await Api_form(formData, `api/v1/Inventory/po/uploadPOExcel`);
        if (res.code === 200) {
          setShowination(true)
          setPoexportdata(true)
          setExcelloges(res?.logs)
          Updateloading(t, GetloadingID, (res?.message));
          setReplenish_OrdersList(res?.data);
          const importLocationId = res?.data?.[0]?.location_id || "";
          const importLocationName = res?.data?.[0]?.location_name || "";
          const importSupplierId = res?.data?.[0]?.supplier_id || "";
          const importSupplierNameVal = res?.data?.[0]?.supplier_name || "";
          setLocation(importLocationId);
          setImportLocationName(importLocationName);
          setSupplier(importSupplierId);
          setImportSupplierName(importSupplierNameVal);
          if (importLocationId) {
            setcommanSelected_loc(importLocationId);
          }
          if (importSupplierId) {
            setcommanSelected_sup(importSupplierId);
          }
        } else {
          Updateloading(t, GetloadingID, (res?.message),'error');
        }
      } catch (err) {
        console.log(err, 'err');
      }
    }
  };
  useEffect(() => { }, [DeliveryDate]);
  useEffect(() => {
    callcolumns();
  }, []);

  const selectOptions = [
    { label: 100, value: 100 },
    { label: 200, value: 200 },
    { label: 300, value: 300 },
  ]
  const callcolumns = async () => {
    try {
      const postdata = {
        name: "tbl_creatreplenish",
      };
      let columnhead = await _Api(
        postdata,
        `api/v1/Inventory/common/getColumnHeads`
      );
      if (columnhead.code === 200) {
        setnewcolumndata(columnhead?.data?.data);
      }
    } catch (err) {
      console.log(err, "err");
    }
  };
  const handleSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };
  const sortedList = useMemo(() => {
    const accessor = SORT_ACCESSORS[sortConfig.key];
    if (!accessor) return Replenish_OrdersList;
    const sorted = [...Replenish_OrdersList].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av === undefined || av === null) return 1;
      if (bv === undefined || bv === null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true });
    });
    return sortConfig.direction === "asc" ? sorted : sorted.reverse();
  }, [Replenish_OrdersList, sortConfig]);
  useEffect(() => {
    const computeStickyLefts = () => {
      let acc = 0;
      const lefts = [];
      for (let i = 0; i < STICKY_COL_COUNT; i++) {
        lefts.push(acc);
        const th = stickyThRefs.current[i];
        if (th) acc += th.offsetWidth;
      }
      setStickyLefts(lefts);
    };
    computeStickyLefts();
    const table = document.getElementById("replenish_order_table");
    const wrap = table?.parentElement;
    const observer =
      wrap && typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(computeStickyLefts)
        : null;
    if (wrap && observer) observer.observe(wrap);
    window.addEventListener("resize", computeStickyLefts);
    return () => {
      window.removeEventListener("resize", computeStickyLefts);
      observer?.disconnect();
    };
  }, [sortedList, newcolumndata]);
  const getStickyColStyle = (visibleIndex, { isHeader, rowIndex } = {}) => {
    if (visibleIndex >= STICKY_COL_COUNT) return undefined;
    let background = "#fff";
    if (!isHeader && typeof rowIndex === "number") {
      background = rowIndex % 2 === 0 ? "#fff" : "#f4f4f4";
    }
    return {
      position: "sticky",
      left: stickyLefts[visibleIndex] || 0,
      // zIndex: isHeader ? 5 : 3,
      zIndex: 3,
      background,
      boxShadow:
        visibleIndex === STICKY_COL_COUNT - 1
          ? "3px 0 5px -2px rgba(0,0,0,0.15)"
          : undefined,
    };
  };
  const { totalPages, currentPage } = aginationsState;
  const visibleOriginalPages = useMemo(
    () => getVisibleOriginalPages(totalPages, hiddenOriginalPages),
    [totalPages, hiddenOriginalPages]
  );
  const displayCurrentPage = Math.max(
    1,
    visibleOriginalPages.indexOf(currentPage) + 1
  );
  const getRowSerial = (index) =>
    showination
      ? index + 1
      : (displayCurrentPage - 1) * (Number(limit) || 100) + index + 1;
  const mergeRowsIntoSelected = (rows, prev) => {
    if (!rows?.length) return prev;
    const existing = new Set(prev.map((item) => String(item.product_id)));
    const additions = [];
    rows.forEach((row, idx) => {
      const productId = getRowProductId(row);
      if (
        !productId ||
        existing.has(String(productId)) ||
        deletedProductIdsRef.current.has(String(productId)) ||
        excludedProductIdsRef.current.has(String(productId))
      ) {
        return;
      }
      existing.add(String(productId));
      additions.push({
        product_id: productId,
        row,
        rowNo: getRowSerial(idx),
      });
    });
    return additions.length ? [...prev, ...additions] : prev;
  };
  const filterDeletedRows = (rows) =>
    (rows || []).filter(
      (row) => !deletedProductIdsRef.current.has(String(getRowProductId(row)))
    );
  const syncSelectedWithPage = (pageRows, prev) =>
    prev
      .filter((sel) => !deletedProductIdsRef.current.has(String(sel.product_id)))
      .map((sel) => {
        const fresh = (pageRows || []).find(
          (r) => String(getRowProductId(r)) === String(sel.product_id)
        );
        return fresh ? { ...sel, row: fresh } : sel;
      });
  const isProductSelected = (productId) =>
    selecteditem.some((item) => String(item.product_id) === String(productId));
  const isPageAllSelected =
    sortedList?.length > 0 &&
    sortedList.every((row) => isProductSelected(getRowProductId(row)));
  const handleSelectAllCheckBox = (event) => {
    const isChecked = event.target.checked;
    const pageIds = (sortedList || [])
      .map((row) => String(getRowProductId(row)))
      .filter(Boolean);
    if (isChecked) {
      pageIds.forEach((id) => excludedProductIdsRef.current.delete(id));
      setSelectAllStatus(true);
      setSelecteditem((prev) => mergeRowsIntoSelected(sortedList, prev));
    } else {
      pageIds.forEach((id) => excludedProductIdsRef.current.add(id));
      setSelecteditem((prev) => prev.filter((item) => !pageIds.includes(String(item.product_id))));
    }
  };
  useEffect(() => {
    if (!SelectAllStatus || !sortedList?.length) return;
    setSelecteditem((prev) => mergeRowsIntoSelected(sortedList, prev));
  }, [SelectAllStatus, sortedList, displayCurrentPage, limit, showination]);

  const handlePaginations = (displayPage) => {
    if (skuList_saved?.length) {
      setPaginationsState({ ...aginationsState, currentPage: displayPage });
      skuBulkSearch(null, displayPage);
      return;
    }
    const originalPage = visibleOriginalPages[displayPage - 1];
    if (!originalPage) return;
    getReplenishList(originalPage, "");
  };
  const getReplenishList = async (current, firstlimit, start_date, end_date) => {

    try {

      if (!commanSelected_vouc) {
        error(t("Please select voucher"));
        return;
      }
      if (!commanSelected_loc) {
        error(t("Please select location"));
        return;
      }
      if (!commanSelected_sup) {
        error(t("Please select Supplier"));
        return;
      }
      let GetloadingID = loading();
      if (!current) {
        setHiddenOriginalPages(new Set());
      }
      const pageLimit = firstlimit ? firstlimit : limit;
      const skipCount =
        !current || pageLimit === "ALL" ? 0 : (current - 1) * Number(pageLimit);
      const postdata = {
        location: commanSelected_loc,
        supplier: commanSelected_sup,
        skip: skipCount,
        "limit": pageLimit,
        "start_date": start_date ? start_date : startdate ? startdate : new Date(),
        "end_date": end_date ? end_date : enddate ? enddate : new Date()
      };
      let resReplenishList = await _Api(
        postdata,
        `api/v1/Inventory/po/getReplenishList`
      );
      if (resReplenishList.code === 200) {
        listFetchedRef.current = true;
        setShowination(false)
        Updateloading(t, GetloadingID, t("Data show successfully!"));
        let pageSize = 1;
        if (postdata.limit === "ALL") {
          pageSize = resReplenishList?.count
        } else {
          pageSize = postdata.limit
        }
        setSkipNumbers(skipCount);
        setPaginationsState({
          ...aginationsState,
          totalPages: Math.ceil(resReplenishList?.count / pageSize),
          currentPage: current ? current : 1
        });
        let pageRows = filterDeletedRows(resReplenishList.data || []);
        if (getqty && current) {
          pageRows = pageRows.map((row) => ({
            ...row,
            order_qty: parseInt(getqty),
          }));
        }
        setReplenish_OrdersList(pageRows);
        setSelecteditem((prev) => syncSelectedWithPage(pageRows, prev));

      } else {
        Updateloading(t, GetloadingID, resReplenishList?.message, 'error');
        setReplenish_OrdersList([]);
      }
    } catch (err) {
      console.log(err, "err");
    }
  };
  const resetReplenishTableState = () => {
    setSelecteditem([]);
    setSelectAllStatus(false);
    setHiddenOriginalPages(new Set());
    setSkuList_saved([]);
    setFiltername("");
    setMasterSearchData([]);
    setExcelloges([]);
    setPoexportdata(false);
    setShowination(false);
    setShowProductSearchBar(false);
    setGetqty(undefined);
    setCuppyqty(undefined);
    setQty(undefined);
    setImportfile(undefined);
    setSortConfig({ key: null, direction: "asc" });
    setSkipNumbers(0);
    setPaginationsState((prev) => ({ ...prev, currentPage: 1 }));
    deletedProductIdsRef.current = new Set();
    excludedProductIdsRef.current = new Set();
  };
  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    if (listFetchedRef.current) {
      resetReplenishTableState();
      set_orderType("Low Stock");
      getReplenishList(undefined, newLimit);
    } else {
      setHiddenOriginalPages(new Set());
      setPaginationsState((prev) => ({ ...prev, currentPage: 1 }));
    }
  };
  const handlePlayReplenishList = () => {
    const isSecondClick = listFetchedRef.current;
    set_orderType("Low Stock");
    if (isSecondClick) {
      resetReplenishTableState();
    } else {
      setHiddenOriginalPages(new Set());
    }
    getReplenishList();
  };
  const po_create_submit = async () => {

    try {
      if (!commanSelected_vouc) {
        error(t("Please select voucher"));
        return;
      }
      if (!commanSelected_loc) {
        error(t("Please select location"));
        return;
      }
      if (!commanSelected_sup) {
        error(t("Please select Supplier"));
        return;
      }
      if (!TransactionDate) {
        error(t("Please select Transaction Date"));
        return;
      }
      if (!DeliveryDate) {
        error(t("Please select Delivery Date"));
        return;
      }
      const resolveSelectedRow = (sel) => {
        const live = (Replenish_OrdersList || []).find(
          (r) => String(getRowProductId(r)) === String(sel.product_id)
        );
        return live || sel?.row;
      };
      const zeroQtyRows = selecteditem.filter((sel) => {
        const qtyVal = parseInt(resolveSelectedRow(sel)?.order_qty, 10);
        return !qtyVal || qtyVal <= 0;
      });
      if (zeroQtyRows.length) {
        const rowNos = zeroQtyRows
          .map((sel) => sel.rowNo)
          .filter((no) => no !== undefined && no !== null)
          .join(", ");
        error(
          rowNos
            ? `${t("Quantity should be greater than 0")} (${t("Row no")} ${rowNos})`
            : t("Quantity should be greater than 0")
        );
        return;
      }
      var product_data = [];
      selecteditem.forEach(function (sel) {
        var product_details = resolveSelectedRow(sel);
        var productId = getRowProductId(product_details) || sel.product_id;
        if (!product_details || !productId) {
          return;
        }
        product_data.push(buildPoLine(product_details, productId));
      });
      if (!product_data.length) {
        error(t("No item Selected!!"));
        return;
      }
      if (product_data.length !== selecteditem.length) {
        error(t("Some selected products could not be added. Please reselect and try again."));
        return;
      }
      setShowSubmitbutton(false);
      let GetloadingID = loading();
      const postdata = {
        voucher_id: commanSelected_vouc,
        location_id: commanSelected_loc,
        locationType,
        supplier_id: commanSelected_sup,
        delivery_date: DeliveryDate,
        transaction_date: TransactionDate,
        note: form_note,
        type: orderType,
        po_data: product_data,
        RefNo: ''
      };
      let resPoOrders = await _Api(postdata, `api/v1/Inventory/po/createPO`);
      if (resPoOrders.code === 200) {
        setShowSubmitbutton(true);
        Updateloading(t, GetloadingID, t("Added successfully!!"));
        const timer = setTimeout(() => {
          navigate("/app/inventory");
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setShowSubmitbutton(true);
        Updateloading(t, GetloadingID, t(resPoOrders?.message), t('error'));
        getReplenishList();
      }
    } catch (err) { }
  };


  const productSearch = useCallback(async () => {
    try {
      const postdata = {
        search: filtername,
      };
      let resp = await _Api(
        postdata,
        `api/v1/Inventory/po/getMasterProductSearch`
      );
      if (resp.code === 200) {
        setMasterSearchData(resp.data);
      } else {
        setMasterSearchData([]);
      }
    } catch (err) {
      console.log(err, "err");
    }
  }, [filtername]);

  const skuBulkSearch = async (skuList, current = 1) => {
    const listToUse = skuList?.length ? skuList : skuList_saved;
    if(!listToUse?.length){
      error(t("Enter Atleast One SKU"));
      return;
    }
    if(!commanSelected_loc){
      error(t("Select Location"))
      return;
    }
    if(!commanSelected_sup){
      error(t("Select Supplier"))
      return;
    }
    try {
       let GetloadingID = loading();
      if (skuList?.length) setSkuList_saved(skuList);
      const postdata = {
        "SKU": listToUse,
        location: commanSelected_loc,
        supplier: commanSelected_sup,
        "start_date": startdate ? startdate : new Date(),
        "end_date": enddate ? enddate : new Date(),
        skip: (current - 1) * (limit === "ALL" ? 0 : Number(limit)),
        limit: limit,
      };
      let resp = await _Api(postdata, "api/v1/Inventory/po/getMasterProductBySKU");
      if(resp.code === 200){
        if(resp?.data?.replenishList?.length){
           Updateloading(t, GetloadingID, t("Replenish data fetched for entered SKU'S"));
          setShowination(false)
          setPoexportdata(true)
          set_orderType("Master Product");
          setReplenish_OrdersList([...Replenish_OrdersList,...(resp?.data?.replenishList || [])]);
          const pageLimit = limit === "ALL" ? resp?.data?.count : limit;
          setSkipNumbers(
            limit === "ALL" ? 0 : (current - 1) * Number(limit)
          );
          setPaginationsState({
            ...aginationsState,
            totalPages: Math.ceil(resp?.data?.total / pageLimit),
            currentPage: current,
          });
        } else {
          if(resp?.message)
           Updateloading(t, GetloadingID, (resp.message), 'error');
        else{
           Updateloading(t, GetloadingID, "Data not found", 'error');
        }
        }
      }
    } catch (err) {
      error(t("No Data found"))
      console.log(err, "err");
    }
  };
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (filtername) {
        productSearch();
      }
    }, 1500); // adjust time as needed
  
    return () => clearTimeout(delayDebounce);
  }, [filtername, productSearch]);
  const getMasterProductById = async (product_id) => {
    try {
      const postdata = {
        id: product_id,
      location: commanSelected_loc,
      supplier: commanSelected_sup,
      "start_date":  startdate ? startdate : new Date(),
      "end_date":  enddate ? enddate : new Date()
      };
      let resp = await _Api(
        postdata,
        `api/v1/Inventory/po/getMasterProductById`
      );
      if (resp.code === 200) {
        const products = extractMasterProducts(resp.data)
          .map(normalizeReplenishProduct)
          .filter(Boolean);
        if (!products.length) {
          error(t("Data not found"));
          return;
        }
        setShowination(true)
        setPoexportdata(true)
        set_orderType("Master Product");
        setReplenish_OrdersList((prev) => {
          const next = [...prev];
          products.forEach((product) => {
            const alreadyAdded = next.some(
              (item) => String(getRowProductId(item)) === String(product.product_id)
            );
            if (!alreadyAdded) {
              next.push(product);
            }
          });
          return next;
        });
      } else {
      }
    } catch (err) {
      console.log(err, "err");
    }
  };

  const getNewMasterProduct = (product_id) => {
    getMasterProductById(product_id);
    setFiltername('');
    setMasterSearchData([]);
  };
const changeorder_qty = (event, row) => {
  const value = event.target.value;
  const order_qty = value === "" ? 0 : Number(value);
  const rowId = getRowProductId(row);
  setReplenish_OrdersList((prev) =>
    prev.map((item) =>
      String(getRowProductId(item)) === String(rowId)
        ? { ...item, order_qty }
        : item
    )
  );
  setSelecteditem((prev) =>
    prev.map((sel) =>
      String(sel.product_id) === String(rowId)
        ? { ...sel, row: { ...sel.row, order_qty } }
        : sel
    )
  );
};


  const changeDates = (lable) => {
    const MainDate = new Date();
    if (lable === 'Today') {
      setStartdate(new Date());
      setEnddate(new Date());
      setDatelable('Today');
      // getReplenishList(1, 100, new Date(), new Date())
    } else if (lable === 'Yesterday') {
      const yesterdaydate = new Date(MainDate.setDate(MainDate.getDate() - 1));
      setStartdate(yesterdaydate);
      setEnddate(yesterdaydate);
      setDatelable('Yesterday');
      // getReplenishList(1, 100, yesterdaydate, yesterdaydate)
    } else if (lable === '7days') {
      const sevendays = new Date(MainDate.setDate(MainDate.getDate() - 7));
      setStartdate(sevendays);
      setEnddate(new Date());
      setDatelable('7days');
      // getReplenishList(1, 100, sevendays, new Date())
    } else if (lable === '30days') {
      const days = new Date(MainDate.setDate(MainDate.getDate() - 30));
      setStartdate(days);
      setEnddate(new Date());
      setDatelable('30days');
      // getReplenishList(1, 100, days, new Date())
    } else if (lable === 'ThisMonth') {
      const firstDay = new Date(MainDate.getFullYear(), MainDate.getMonth(), 1);
      const lastDay = new Date(MainDate.getFullYear(), MainDate.getMonth() + 1, 0);
      setStartdate(firstDay);
      setEnddate(lastDay);
      setDatelable('ThisMonth');
      // getReplenishList(1, 100, firstDay, lastDay)
    } else if (lable === "LastMonth") {
      const firstDay = new Date(MainDate.getFullYear(), MainDate.getMonth() - 1, 1);
      const lastDay = new Date(MainDate.getFullYear(), MainDate.getMonth(), 0);
      setStartdate(firstDay);
      setEnddate(lastDay);
      // getReplenishList(1, 100, firstDay, lastDay)
      setDatelable('LastMonth');
    }

  }
  const editproduct_submit = () => {
    // oneproductindex is the row's position in the sorted view, not in Replenish_OrdersList,
    // so write the edit back by product id; otherwise a sorted table updates the wrong row.
    const editedId = String(getRowProductId(getoneproduct));
    setReplenish_OrdersList((prev) =>
      prev.map((row) => (String(getRowProductId(row)) === editedId ? getoneproduct : row))
    );
  }
  const deleteProduct = () => {
    const pageRows = sortedList || [];
    if (!pageRows.length) return;
    const pageIds = new Set(pageRows.map((row) => String(getRowProductId(row))));
    const removedIds = new Set(
      selecteditem
        .filter((sel) => pageIds.has(String(sel.product_id)))
        .map((sel) => String(sel.product_id))
    );
    if (!removedIds.size) return;

    const nextDeleted = new Set(deletedProductIdsRef.current);
    removedIds.forEach((id) => nextDeleted.add(id));
    deletedProductIdsRef.current = nextDeleted;

    const remaining = (Replenish_OrdersList || []).filter(
      (row) => !removedIds.has(String(getRowProductId(row)))
    );
    setSelecteditem((prev) =>
      prev.filter((sel) => !removedIds.has(String(sel.product_id)))
    );

    const deletedWholePage = remaining.length === 0;
    if (deletedWholePage && !showination) {
      setSelectAllStatus(true);
    }
    if (!deletedWholePage || showination) {
      setReplenish_OrdersList(remaining);
      return;
    }

    const deletedOriginal = currentPage;
    const nextHidden = new Set(hiddenOriginalPages);
    nextHidden.add(deletedOriginal);
    setHiddenOriginalPages(nextHidden);
    const nextOriginal = getNextOriginalPage(
      deletedOriginal,
      totalPages,
      nextHidden
    );
    if (nextOriginal) {
      getReplenishList(nextOriginal, "");
    } else {
      setReplenish_OrdersList([]);
    }
  }
  const cuppyProduct = () => {
    const mainarray = [...Replenish_OrdersList];
    for (var i = 0; i < mainarray.length; i++) {

      mainarray[i] = {
        ...mainarray[i],
        'order_qty': parseInt(cuppyqty)
      }
    }
    setGetqty(mainarray[0]?.order_qty)
    setReplenish_OrdersList(mainarray);
    setSelecteditem([]);
    setSelectAllStatus(false);
    excludedProductIdsRef.current = new Set();
  }
  const selectedproduct = (e) => {
    const productId = e.target.value;
    const productIndex = parseInt(e.target.dataset.key, 10);
    const isChecked = e.target.checked;
    const row = sortedList[productIndex];

    if (isChecked) {
      excludedProductIdsRef.current.delete(String(productId));
      setSelecteditem((prev) =>
        prev.some((item) => String(item.product_id) === String(productId))
          ? prev
          : [...prev, { product_id: productId, row, rowNo: getRowSerial(productIndex) }]
      );
    } else {
      excludedProductIdsRef.current.add(String(productId));
      setSelecteditem((prev) => prev.filter((item) => String(item.product_id) !== String(productId)));
    }
  };

  const canExportReplenish = () => {
    if (!commanSelected_vouc) {
      error(t("Please select voucher"));
      return false;
    }
    if (!commanSelected_loc) {
      error(t("Please select location"));
      return false;
    }
    if (poexportdata && !selecteditem.length) {
      error(t("No item Selected!!"));
      return false;
    }
    return true;
  };

  const openReplenishExportFormatModal = () => {
    setIsOpen(false);
    if (!canExportReplenish()) return;
    const el = document.getElementById("replenishOrderExportFormatModal");
    if (!el) return;
    if (window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(el).show();
      return;
    }
    if (window.$) {
      window.$(el).modal("show");
    }
  };

  const poimport = async (format = poExportFormat) => {
    if (!canExportReplenish()) return;
    let GetloadingID = loading();
    try {
      let selectprodutid = [...selecteditem]
      let product_id = []
      if (selectprodutid?.length) {
        selectprodutid.forEach((result) => {
          product_id.push(result?.product_id)
        })
      }
      let postdata = {
        "product_id": selecteditem.length ? product_id : [],
        "start_date": startdate ? startdate : new Date(),
        "end_date": enddate ? enddate : new Date(),
        "location": commanSelected_loc,
        format: format || "xlsx",
      }
      let res = await _Api(postdata, "api/v1/Inventory/po/geReplenishListExport")
      if (res?.code === 200) {
        Updateloading(t, GetloadingID, (res?.message));
      } else {
        Updateloading(t, GetloadingID, (res.errors), 'error');
      }
    }
    catch (err) {
      console.log(err, "err")
    }
  }

  const getProductByTblLowStock = useCallback(async (product_id) => {
    try {
      const postdata = {
        "ids": product_id ? product_id : ["679079bb30d7e1fad61f0e8a", "6790794e30d7e1fad61f0d16"]
      };
      let resp = await _Api(
        postdata,
        `api/v1/Inventory/po/getLowStockById`
      );
      if (resp.code === 200 && resp.data.length) {
        setShowination(true)
        setPoexportdata(true)
        set_orderType("Master Product");
        setReplenish_OrdersList([...Replenish_OrdersList, ...resp.data]);
      } else {
      }
    } catch (err) {
      console.log(err, "err");
    }
  }, [Replenish_OrdersList, newcolumndata]);

  useEffect(() => {
    getProductByTblLowStock(state?.lowStockID);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.lowStockID, state?.hidelocationid]);

  return (
    <React.Fragment>
      <div className="main-body" style={{paddingTop: "0px"}}>
      <div className="create-po-sticky-header">
        <div className="row" id="new-brandcumcategory">
          <div className="main-content-wrapper-body-top-bar d-flex align-items-center justify-content-between">
            <div className="main-content-wrapper-body-top-bar-left">
              <h1 className="main-content-wrapper-body-top-bar-left-heading fw-semibold mb-0">
                {t("Transaction")} <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5L13 10L8 15" stroke="#A0A0A0" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                <span>{t("Create Purchase Order")}</span>
              </h1>
            </div>
          </div>
        </div>
        <div className="container" id="style-create-po-page">
          <div className="row">
            <div className="top-heading-area">
              <div className="top-heading-area-left">
                <span onClick={() => navigate(-1)} className="page-back-btn">
                  <img
                    src={base_url + "images/icons/arrow-left.png"}
                    alt=""
                  />
                </span>
                <img src={base_url + "images/icons/home-icon.png"} alt="" />
                <VoucherList
                  setcommanSelected_vouc={setcommanSelected_vouc}
                  group={"Purchase_order"}
                ></VoucherList>
              </div>

            </div>
            <div className="col-lg-12">

              <div className="main-body-top-tab-bar create-purchase-top-bar">
                <div
                  className="main-body-top-tab-bar-left-col nav nav-pills"
                  role="tablist"
                >
                  <Link
                    to="/app/inventory/createpo"
                    className="main-body-top-tab-bar-left-col-a nav-link tab-hover table-refresh"
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      <img
                        className="tab-hover-img-1"
                        src={base_url + "images/icons/price-list-icon.png"}
                        alt=""
                      />
                      <img
                        className="tab-hover-img-2"
                        src={
                          base_url + "images/icons/price-list-icon-green.png"
                        }
                        alt=""
                      />
                      {t("POS Order")}
                    </span>
                  </Link>
                  <Link
                    to="/app/inventory/createpoReplenishOrder"
                    className="main-body-top-tab-bar-left-col-a nav-link tab-hover table-refresh active"
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      <img
                        className="tab-hover-img-1"
                        src={base_url + "images/icons/replenish-icon.png"}
                        alt=""
                      />
                      <img
                        className="tab-hover-img-2"
                        src={base_url + "images/icons/replenish-icon-green.png"}
                        alt=""
                      />
                      {t("Replenish Order")}
                    </span>
                  </Link>
                  <Link
                    to="/app/inventory/createPOReworkOrder"
                    className="main-body-top-tab-bar-left-col-a nav-link tab-hover table-refresh"
                  >
                    <span className="main-body-top-tab-bar-left-col-a-text">
                      <img
                        className="tab-hover-img-1"
                        src="https://staticassets2.gis247.net/assets/v2/images/icons/free-stock.png"
                        alt=""
                        width={24}
                        height={24}
                        style={{ width: 24, height: 24, objectFit: "contain" }}
                      />
                      <img
                        className="tab-hover-img-2"
                        src="https://staticassets2.gis247.net/assets/v2/images/icons/free-stock.png"
                        alt=""
                        width={24}
                        height={24}
                        style={{ width: 24, height: 24, objectFit: "contain" }}
                      />
                      {t("Rework Order")}
                    </span>
                  </Link>
                </div>
                {/* <div className="main-body-top-tab-bar-right-col"                >
                  <div className="main-body-top-tab-bar-right-col-search" ref={inputRef}>
                    {isInputVisible ? (
                      <div className="wrap-expandable-input-field">
                        <button
                          // onClick={(e) => filterbyname(filtername)}
                          className="main-body-top-status-bar-filter-search"
                        >
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.9269 14.04L17.4 17.4M16.28 8.43998C16.28 12.7699 12.7699 16.28 8.44001 16.28C4.11009 16.28 0.600006 12.7699 0.600006 8.43998C0.600006 4.11006 4.11009 0.599976 8.44001 0.599976C12.7699 0.599976 16.28 4.11006 16.28 8.43998Z"
                              stroke="#A4A4A4"
                              strokeLinecap="round"
                            />
                          </svg>
                        </button>

                        <input
                          type="text"
                          value={filtername}
                          className="main-body-top-status-bar-filter-input"
                          placeholder={t("Search SKU/Metal/Stone")}
                        // onKeyPress={(e) =>
                        //   e.key === "Enter" && filterbyname(e.target.value)
                        // }
                        // onChange={(e) => setFiltername(e.target.value)}
                        />
                        {filtername ? (
                          <svg
                            onClick={(e) => {
                              setFiltername("");
                              // filterbyname("");
                            }}
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13 13L7 7M7 7L1 1M7 7L13 1M7 7L1 13"
                              stroke="#767676"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          ""
                        )}
                      </div>
                    ) : (
                      <button
                        className="search-icon"
                        onClick={() => setIsInputVisible(true)}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.9269 14.04L17.4 17.4M16.28 8.43998C16.28 12.7699 12.7699 16.28 8.44001 16.28C4.11009 16.28 0.600006 12.7699 0.600006 8.43998C0.600006 4.11006 4.11009 0.599976 8.44001 0.599976C12.7699 0.599976 16.28 4.11006 16.28 8.43998Z"
                            stroke="#A4A4A4"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    )}
                  </div>

                  <SortIcon />
                  <PrintIcon />
                </div> */}

              </div>
              <div className="tab-content">
                <div className="tab-pane active">
                  <div className="main-body-top-inputs-bar">
                    <div className="main-body-top-inputs-bar-col-left">
                      <label className="po-num-label  po-num-green">
                        {t("po-XXXX")}
                      </label>
                      <LocationList
                        setcommanSelected_loc={setcommanSelected_loc}
                        commanSelected_vouc={commanSelected_vouc}
                        setpo={"setpo"}
                        addclass={state?.hidelocationid ? 'row_inactive' : ""}
                        locationid={state?.hidelocationid || location || ""}
                        locationname={importLocationName || ""}
                        // locationType={locationType}
                        // setLocationType={setLocationType}
                      />
                      <SupplierList
                        setcommanSelected_sup={setcommanSelected_sup}
                        supplierid={state?.hidesupplierid || supplier || ""}
                        suppliername={importSupplierName || ""}
                      />
                      <TransAndDeliverDate TransactionDate={TransactionDate} DeliveryDate={DeliveryDate} setTransactionDate={setTransactionDate} setDeliveryDate={setDeliveryDate} />
                    </div>
                    <div className="main-body-top-inputs-bar-col-right">
                      {excelloges?.length ?
                        <div className="dropdown user-log-dropdown d-flex align-items-center">
                          <button className="btn-style stock-take-inner-row-col-mid-header-right-btn mx-1 dropdown-toggle"
                            data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                            <img className="note" src={base_url + "images/icons/note-board-icon-dark.png"} alt="" />
                          </button>

                          <ul className="dropdown-menu">
                            <ul className="nav nav-tabs user-log-tabs" id="myTab" role="tablist">
                              <li className="nav-item" role="presentation">
                                <button className="nav-link active" id="user-tab" data-bs-toggle="tab" data-bs-target="#user-tab-pane" type="button" role="tab" aria-controls="user-tab-pane" aria-selected="true">{t("Excel Import Loges")}</button>
                              </li>
                            </ul>
                            <div className="tab-content" id="myTabContent">
                              <div className="tab-pane fade show active" id="user-tab-pane" role="tabpanel" aria-labelledby="user-tab" tabIndex="0">
                                <table>
                                  <tbody>
                                    {excelloges?.length ? excelloges.map((result, key) => {
                                      return (
                                        <tr key={key}>
                                          <td>
                                            <b className="fw-semibold">{result?.SKU ? result?.SKU : ""}</b><br />
                                            <span>{result?.message ? result?.message : ""}</span><br />
                                          </td>

                                        </tr>
                                      )
                                    })
                                      : ""}

                                  </tbody>
                                </table>
                              </div>

                            </div>
                          </ul>
                        </div> : ""}
                      <button
                        className={
                          ShowSubmitbutton
                            ? "main-body-top-inputs-bar-col-right-submit-btn com-btn "
                            : "main-body-top-inputs-bar-col-right-submit-btn com-btn d-block  deactive"
                        }
                        data-bs-toggle="modal"
                        data-bs-target="#createPurchaseModal"
                      >
                        {t("Submit")}
                      </button>
                    </div>
                  </div>
                  <div className="main-body-top-status-bar">
                    <Select
                      className=" select-set select p-0 main-content-wrapper-body-top-status-bar-select-num"
                      options={selectOptions}
                      value={selectOptions.find((opt) => opt.value === limit) || { label: limit, value: limit }}
                      onChange={(e) => handleLimitChange(e.value)}
                      isSearchable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />

                    <div className="main-body-top-status-bar-multi-date-picker">
                      <div className="main-body-top-tab-bar-right-col-search" ref={productSearchBar}>
                        {ShowProductSearchBar ? (
                          <div className="wrap-expandable-input-field">
                            <div className="main-body-top-status-bar-filter">
                              <button className="main-body-top-status-bar-filter-search">
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 18 18"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M13.9269 14.04L17.4 17.4M16.28 8.43998C16.28 12.7699 12.7699 16.28 8.44001 16.28C4.11009 16.28 0.600006 12.7699 0.600006 8.43998C0.600006 4.11006 4.11009 0.599976 8.44001 0.599976C12.7699 0.599976 16.28 4.11006 16.28 8.43998Z"
                                    stroke="#606060"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                              <input
                                type="text"
                                value={filtername}
                                className="main-body-top-status-bar-filter-input"
                                placeholder={t("Search")}
                                onChange={(e) => {
                                  setFiltername(e.target.value);
                                  setHighlightedIndex(0); // reset highlight
                                }}
                                onKeyDown={(e) => {
                                  if (!masterSearchData?.length) return;
                              
                                  if (e.key === "ArrowDown") {
                                    e.preventDefault();
                                    setHighlightedIndex((prev) => (prev + 1) % masterSearchData.length);
                                  } else if (e.key === "ArrowUp") {
                                    e.preventDefault();
                                    setHighlightedIndex((prev) =>
                                      prev === 0 ? masterSearchData.length - 1 : prev - 1
                                    );
                                  } else if (e.key === "Enter") {
                                    const selected = masterSearchData[highlightedIndex];
                                    if (selected) {
                                      getNewMasterProduct(selected._id);
                                    }
                                  }
                                }}
                              
                              />
                              {filtername ? <svg onClick={(e) => setFiltername("")} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 13L7 7M7 7L1 1M7 7L13 1M7 7L1 13" stroke="#767676" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                                : ""}
                            </div>
                              {filtername ?
                                <div className="dropdown-menu custom-search-dropdown catalog-all-search-dropdown po-search-dropdown show">
                                  <ul id="item_list_cardul" className="po-search-dropdown-list" ref={listRef}>
                                    {masterSearchData?.length ? masterSearchData?.map((result, index) => {
                                      return (
                                        <li
                                          onClick={() => getNewMasterProduct(result._id)}
                                          key={index}
                                          className={`dropdown-item pos-search-result-item${highlightedIndex === index ? " focused" : ""}`}
                                        >
                                          <div className="pos-search-result-thumb">
                                            {result.main_image ? (
                                              <img
                                                src={result.main_image}
                                                alt=""
                                                onError={(e) => {
                                                  e.target.onerror = null;
                                                  e.target.src =
                                                    "https://gis247.s3.us-east-2.amazonaws.com/placeholder-blank.png";
                                                }}
                                              />
                                            ) : null}
                                          </div>
                                          <div className="pos-search-result-text">
                                            <div className="pos-search-result-name">{result.name}</div>
                                            <div className="pos-search-result-meta">{result.SKU}</div>
                                          </div>
                                        </li>
                                      )
                                    }) : ""}
                                  </ul>
                                </div>
                                : null}
                          </div>
                        ) : (
                          <button
                            className="search-icon"
                            onClick={() => setShowProductSearchBar(true)}
                          >
                            <svg
                              width="18"
                              height="18"
                              viewBox="0 0 18 18"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M13.9269 14.04L17.4 17.4M16.28 8.43998C16.28 12.7699 12.7699 16.28 8.44001 16.28C4.11009 16.28 0.600006 12.7699 0.600006 8.43998C0.600006 4.11006 4.11009 0.599976 8.44001 0.599976C12.7699 0.599976 16.28 4.11006 16.28 8.43998Z"
                                stroke="#606060"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        )}
                      </div>

                      <div className="print-export-dropdown-selected-icon">
                        <button className={
                          selecteditem.length > 0
                            ? "deletebtn btn-style d-block"
                            : "d-none"
                        } onClick={deleteProduct}>
                          <svg width="20" height="20" viewBox="0 0 24 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8.43143 4.40639L9.32429 0.834961H14.6814L15.5743 4.40639M22.7171 4.40639H3.07429L4.86 25.835H19.1457L20.9314 4.40639H1.28857H22.7171ZM12.0029 9.76353V20.4778V9.76353ZM16.4671 9.76353L15.5743 20.4778L16.4671 9.76353ZM7.53857 9.76353L8.43143 20.4778L7.53857 9.76353Z" stroke="#5CC7C1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                          </svg>

                        </button>
                      </div>
                      <div className="print-export-dropdown-selected-icon">
                        <button className={
                          cuppyqty
                            ? "deletebtn btn-style d-block"
                            : "d-none"
                        } onClick={cuppyProduct}  >
                          <img src={base_url + "images/icons/clone.svg"} alt="" />
                        </button>
                      </div>
                      <div className="main-body-top-status-bar-video-btn tooltip-area-up">
                        <span className="tooltip-area-up-text">{t("Run Replenishment")}</span>
                        <svg
                          onClick={handlePlayReplenishList}
                          width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 18.125C8.39303 18.125 6.82214 17.6485 5.486 16.7557C4.14985 15.8629 3.10844 14.594 2.49348 13.1093C1.87852 11.6247 1.71762 9.99099 2.03112 8.4149C2.34463 6.8388 3.11846 5.39106 4.25476 4.25476C5.39106 3.11846 6.8388 2.34463 8.4149 2.03112C9.99099 1.71762 11.6247 1.87852 13.1093 2.49348C14.594 3.10844 15.8629 4.14985 16.7557 5.486C17.6485 6.82214 18.125 8.39303 18.125 10C18.125 12.1549 17.269 14.2215 15.7452 15.7452C14.2215 17.269 12.1549 18.125 10 18.125ZM10 3.125C8.64026 3.125 7.31105 3.52822 6.18046 4.28365C5.04987 5.03908 4.16868 6.11281 3.64833 7.36906C3.12798 8.6253 2.99183 10.0076 3.2571 11.3412C3.52238 12.6749 4.17716 13.8999 5.13864 14.8614C6.10013 15.8228 7.32514 16.4776 8.65876 16.7429C9.99238 17.0082 11.3747 16.872 12.631 16.3517C13.8872 15.8313 14.9609 14.9501 15.7164 13.8195C16.4718 12.689 16.875 11.3597 16.875 10C16.875 8.17664 16.1507 6.42796 14.8614 5.13864C13.5721 3.84933 11.8234 3.125 10 3.125ZM8.97875 13.03C8.88541 13.092 8.77582 13.1251 8.66375 13.125C8.59548 13.1278 8.52731 13.1171 8.4632 13.0935C8.39908 13.0699 8.34028 13.0338 8.29019 12.9873C8.2401 12.9408 8.19972 12.8848 8.17136 12.8227C8.14301 12.7605 8.12726 12.6933 8.125 12.625V7.375C8.12559 7.26918 8.16169 7.16662 8.2275 7.08375C8.3173 6.97663 8.44341 6.90632 8.58175 6.88627C8.72009 6.86621 8.86097 6.8978 8.9775 6.975L12.9025 9.6C12.9501 9.62931 12.9924 9.66651 13.0275 9.71C13.0685 9.76222 13.0979 9.82251 13.1139 9.88692C13.1298 9.95133 13.132 10.0184 13.1202 10.0837C13.1084 10.149 13.0829 10.211 13.0453 10.2658C13.0078 10.3205 12.9591 10.3667 12.9025 10.4013L8.97875 13.03Z" fill="#52CBC4" />
                        </svg>


                      </div>
                      <div className="main-body-top-status-bar-note-button common-dropdown replenish-date" >
                        <button className="dropdown-toggle btn-style align-items-center d-flex" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 4.66667V8L10.5 10.5M15.5 8C15.5 12.1421 12.1421 15.5 8 15.5C3.85786 15.5 0.5 12.1421 0.5 8C0.5 3.85786 3.85786 0.5 8 0.5C12.1421 0.5 15.5 3.85786 15.5 8Z" stroke="currentcolor" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>

                          {t(datelable)}
                        </button>
                        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton1">
                          <li className={`dropdown-item ${datelable === 'Today' || datelable === "Replenish" ? "active" : ""}`} onClick={() => changeDates('Today')} >{t("Today")}</li>
                          <li className={`dropdown-item ${datelable === 'Yesterday' ? "active" : ""}`} onClick={() => changeDates('Yesterday')}>{t("Yesterday")}</li>
                          <li className={`dropdown-item ${datelable === '7days' ? "active" : ""}`} onClick={() => changeDates('7days')}>{t("Last 7 days")}</li>
                          <li className={`dropdown-item ${datelable === '30days' ? "active" : ""}`} onClick={() => changeDates('30days')}>{t("Last 30 days")}</li>
                          <li className={`dropdown-item ${datelable === 'ThisMonth' ? "active" : ""}`} onClick={() => changeDates('ThisMonth')}>{t("This Month")}</li>
                          <li className={`dropdown-item ${datelable === 'LastMonth' ? "active" : ""}`} onClick={() => changeDates('LastMonth')}>{t("Last Month")}</li>
                          <li className={`dropdown-item ${datelable === 'Custom Range' ? "active" : ""}`}
                            onClick={() => datePickerRef.current.setOpen(true)}>{t("Custom Range")}</li>
                        </ul>
                        <DatePicker
                          selectsRange={true}
                          monthsShown={2}
                          startDate={startdate}
                          endDate={enddate}
                          onChange={(update) => {
                            const startDate = update[0];
                            const endDate = update[1];
                            setStartdate(startDate);
                            setEnddate(endDate);
                            setDatelable("Custom Range");
                            const modifiedStartDate = new Date(startDate);
                            modifiedStartDate.setHours(12, 10, 10, 999);
                            const modifiedEndDate = new Date(endDate);
                            modifiedEndDate.setHours(12, 10, 10, 999);
                            // getReplenishList(1, 100, modifiedStartDate, modifiedEndDate);
                          }}
                          dateFormat="yyyy-MM-dd"
                          ref={datePickerRef}
                          autoComplete='none'
                        />
                      </div>
                      <div
                        className="main-body-top-status-bar-note-button"
                        data-bs-toggle="modal"
                        data-bs-target="#skuModal"
                      >
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 3h12M2 7h8M2 11h5" stroke="currentcolor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span>{t("SKU")}</span>
                      </div>
                      <div
                        className="main-body-top-status-bar-note-button"
                        data-bs-toggle="modal"
                        data-bs-target="#lsp-notesModal"
                      >
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.83333 5.08333H8M3.83333 8H10.5M3.83333 13V14.9463C3.83333 15.3903 3.83333 15.6123 3.92436 15.7263C4.00352 15.8255 4.12356 15.8832 4.25045 15.8831C4.39636 15.8829 4.56973 15.7442 4.91646 15.4668L6.90434 13.8765C7.31043 13.5517 7.51347 13.3892 7.73957 13.2737C7.94017 13.1712 8.15369 13.0963 8.37435 13.051C8.62306 13 8.88308 13 9.40312 13H11.5C12.9001 13 13.6002 13 14.135 12.7275C14.6054 12.4878 14.9878 12.1054 15.2275 11.635C15.5 11.1002 15.5 10.4001 15.5 9V4.5C15.5 3.09987 15.5 2.3998 15.2275 1.86502C14.9878 1.39462 14.6054 1.01217 14.135 0.772484C13.6002 0.5 12.9001 0.5 11.5 0.5H4.5C3.09987 0.5 2.3998 0.5 1.86502 0.772484C1.39462 1.01217 1.01217 1.39462 0.772484 1.86502C0.5 2.3998 0.5 3.09987 0.5 4.5V9.66667C0.5 10.4416 0.5 10.8291 0.585185 11.147C0.816353 12.0098 1.49022 12.6836 2.35295 12.9148C2.67087 13 3.05836 13 3.83333 13Z" stroke="currentcolor" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>

                        <span>{t("Note")}</span>
                      </div>
                      <div className="main-body-top-status-bar-print-export-dropdown dropdown">
                        <button
                          className="btn dropdown-toggle"
                          type="button"
                          id="dropdownMenuButton2"
                          aria-expanded={isOpen ? "true" : "false"}
                          // data-bs-toggle="dropdown"

                          onClick={toggleDropdown}
                          style={{width:'20px',marginLeft:'-8px'}}
                        >
                          <svg width="4" height="10" viewBox="0 0 4 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M2 10C1.30964 10 0.75 9.44036 0.75 8.75C0.75 8.05964 1.30964 7.5 2 7.5C2.69036 7.5 3.25 8.05964 3.25 8.75C3.25 9.44036 2.69036 10 2 10ZM2 6.25C1.30964 6.25 0.75 5.69036 0.75 5C0.75 4.30964 1.30964 3.75 2 3.75C2.69036 3.75 3.25 4.30964 3.25 5C3.25 5.33152 3.1183 5.64946 2.88388 5.88388C2.64946 6.1183 2.33152 6.25 2 6.25ZM2 2.5C1.30964 2.5 0.75 1.94036 0.75 1.25C0.75 0.559644 1.30964 0 2 0C2.69036 0 3.25 0.559644 3.25 1.25C3.25 1.58152 3.1183 1.89946 2.88388 2.13388C2.64946 2.3683 2.33152 2.5 2 2.5Z" fill="#2A2A2A" />
                          </svg>

                        </button>
                        <ul
                          ref={secondRowOption}
                          className=
                          {`lsp-drop-down dropdown-menu ${isOpen ? "show" : ""
                            }`}
                          aria-labelledby="dropdownMenuButton1"

                        >
                          <li
                            className="dropdown-item"
                            onClick={openReplenishExportFormatModal}
                          >
                            <div className="dropdown-item-img">
                              <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0.5 19.1663H15.5V5.59051L10.7417 0.833008H0.5V19.1663ZM10.5 1.66634H10.6667L14.6667 5.66634V5.83301H10.5V1.66634ZM1.33333 1.66634H9.66667V6.66634H14.6667V18.333H1.33333V1.66634ZM11.2425 8.33301L8.515 12.083L11.2425 15.833H10.2125L8 12.7913L5.7875 15.833H4.75833L7.48583 12.083L4.7575 8.33301H5.7875L8 11.3747L10.2125 8.33301H11.2425Z" fill="#A4A4A4" />
                              </svg>

                            </div>
                            <span>{t("Export Replenish Order")}</span>
                          </li>
                          <li
                            className="dropdown-item"
                            data-bs-toggle="modal"
                            data-bs-target="#poPuSampleFormatModal"
                          >
                            <div className="dropdown-item-img">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 1.333v9.334M8 10.667L5.333 8M8 10.667L10.667 8M2.667 14h10.666" stroke="#A4A4A4" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                            <span>{t("Sample")}</span>
                          </li>
                          <li className="dropdown-item" data-bs-toggle="modal" data-bs-target="#ReplenishOrderImportModal">
                            <div className="dropdown-item-img">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.33333 10.6703L1.33333 13.7438C1.33333 14.2095 1.50893 14.6562 1.82149 14.9856C2.13405 15.315 2.55797 15.5 3 15.5H13C13.442 15.5 13.8659 15.315 14.1785 14.9856C14.4911 14.6562 14.6667 14.2095 14.6667 13.7438V10.6703M8.00093 0.5V10.4521M8.00093 10.4521L11.8105 6.64941M8.00093 10.4521L4.1914 6.64941" stroke="#A4A4A4" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>

                            </div>
                            <span>{t("Import Replenish Order")}</span>
                          </li>
                          <li
                            className="dropdown-item"
                            // data-bs-toggle="modal"
                            // data-bs-target="#columnModal"
                            onClick={() => {
                              setShowEditColumns(true);
                            }}
                            ref={closeEditColumns}
                          >
                            {showEditColumns && <EditColumnSubmenu
                              tablehead={tbl_createreplenishHead}
                              tblName={"tbl_creatreplenish"}
                              setnewcolumndata={setnewcolumndata}
                              calldatatable={getReplenishList}
                            ></EditColumnSubmenu>}

                            <div className="dropdown-item-img">
                              <svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2.5 6V4C2.5 3.46957 2.67559 2.96086 2.98816 2.58579C3.30072 2.21071 3.72464 2 4.16667 2H15.8333C16.2754 2 16.6993 2.21071 17.0118 2.58579C17.3244 2.96086 17.5 3.46957 17.5 4V6M2.5 6V12M2.5 6H7.5M17.5 6V12M17.5 6H7.5M2.5 12V16C2.5 16.5304 2.67559 17.0391 2.98816 17.4142C3.30072 17.7893 3.72464 18 4.16667 18H7.5M2.5 12H7.5M7.5 6V12M17.5 12V16C17.5 16.5304 17.3244 17.0391 17.0118 17.4142C16.6993 17.7893 16.2754 18 15.8333 18H7.5M17.5 12H7.5M7.5 18V12M12.5 6V18" stroke="#A4A4A4" strokeLinecap="round" strokeLinejoin="round" />
                                <rect x="14" y="8" width="10" height="10" rx="5" fill="white" />
                                <path d="M20.1132 10.8178L16.3251 14.6059L16.2557 15.8537L17.5036 15.7844L21.2917 11.9963M20.1132 10.8178L20.6752 10.2558C21.0006 9.93036 21.5282 9.93036 21.8537 10.2558C22.1791 10.5812 22.1791 11.1089 21.8537 11.4343L21.2917 11.9963M20.1132 10.8178L21.2917 11.9963" stroke="#A4A4A4" strokeWidth="0.75" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>

                            </div>
                            <span>{t("Edit columns")}</span>
                            <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M1 1L6 6L1 11" stroke="#A4A4A4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>

                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        <div className="container" id="style-create-po-page-body">
          <div className="row">
            <div className="col-lg-12">
              <div className="tab-content">
                <div id="replenish_order" className="tab-pane active">
                  <div className="common-sub-table-wrap add-row">
                    <div className="main-body-main-table-wrap position-relative">
                      <table
                        id="replenish_order_table"
                        className="common-table table-striped main-table w-100"
                      >
                        <thead>
                          <tr>
                            {(() => {
                              let visibleIndex = -1;
                              return newcolumndata?.map((col, index) => {
                                if (!col?.status) return null;
                                visibleIndex += 1;
                                const stickyIdx = visibleIndex;
                                const stickyStyle = getStickyColStyle(stickyIdx, { isHeader: true });
                                const stickyRef =
                                  stickyIdx < STICKY_COL_COUNT
                                    ? (el) => {
                                        stickyThRefs.current[stickyIdx] = el;
                                      }
                                    : undefined;
                                if (col.name === "check_box") {
                                  return (
                                    <th
                                      key={index}
                                      ref={stickyRef}
                                      className={stickyStyle ? "sticky-col" : undefined}
                                      style={stickyStyle}
                                    >
                                      <div className="com-check">
                                        <input
                                          id="th_comman_checkbox"
                                          onChange={handleSelectAllCheckBox}
                                          checked={isPageAllSelected}
                                          className={col.class}
                                          type="checkbox"
                                        />
                                        <label
                                          htmlFor="th_comman_checkbox"
                                          className="com-check-label"
                                        />
                                      </div>
                                    </th>
                                  );
                                }
                                const sortKey = SORT_KEYS[index];
                                const active = sortConfig.key === sortKey;
                                return (
                                  <th
                                    key={index}
                                    ref={stickyRef}
                                    className={`${col.class || ""}${stickyStyle ? " sticky-col" : ""}`.trim()}
                                    onClick={sortKey ? () => handleSort(sortKey) : undefined}
                                    style={{
                                      ...(sortKey ? { cursor: "pointer" } : {}),
                                      ...(stickyStyle || {}),
                                    }}
                                  >
                                    {t(col.lable)}
                                    {col.name === "Qty" ? <QtyFormulaInfo t={t} /> : null}
                                    {sortKey ? (
                                      <div className="up-down-arrow-btn">
                                        <img
                                          className={
                                            "up-down-arrow-img up-down-arrow-img-forward" +
                                            (active && sortConfig.direction === "desc" ? "" : " active")
                                          }
                                          src={base_url + "images/icons/up-down-arrow.png"}
                                          alt=""
                                        />
                                        <img
                                          className={
                                            "up-down-arrow-img up-down-arrow-img-reverse" +
                                            (active && sortConfig.direction === "desc" ? " active" : "")
                                          }
                                          src={base_url + "images/icons/up-down-arrow-reverse.png"}
                                          alt=""
                                        />
                                      </div>
                                    ) : null}
                                  </th>
                                );
                              });
                            })()}
                          </tr>
                        </thead>
                        <tbody>
                          {sortedList?.map((result, index) => {
                            const rowProductId = getRowProductId(result);
                            const cells = [
                              <td key="cb">
                                <div className="com-check checkbox-width">
                                  <input
                                    checked={isProductSelected(rowProductId)}
                                    data-key={index}
                                    className="allocation_OrdersList createreplenish"
                                    type="checkbox"
                                    onChange={selectedproduct}
                                    value={rowProductId}
                                    onClick={() => setQty(result.order_qty)}
                                    data-id={rowProductId}
                                    data-qty={result?.order_qty}
                                    id={`${rowProductId}_${index}`}
                                  />
                                  <label
                                    htmlFor={`${rowProductId}_${index}`}
                                    className="com-check-label"
                                  />
                                </div>
                              </td>,
                              <td key="no">{getRowSerial(index)}</td>,
                              <td key="img">
                                <div className="table-pro-img" onClick={() => setImageid(result?.product_id)} data-bs-toggle="modal" data-bs-target="#ProductViewModal">
                                  <img src={result.main_image} alt="" />
                                </div>
                              </td>,
                              <td key="name">
                                <div className="tooltip-area-up">
                                  <span className="table-pro-tooltip">{result.name}</span>
                                  <span className="tooltip-area-up-text text-white">
                                    {result.name}
                                  </span>
                                </div>
                              </td>,
                              <td
                                key="sku"
                                className="po-no link-color cursor-pointer col-highlight"
                                onClick={() => { setOneproduct(result); setoneproductindex(index) }}
                                data-bs-toggle="modal"
                                data-bs-target="#ProductInfoModal"
                              >
                                {result.SKU}
                              </td>,
                              <td key="metal" className="col-highlight">{result.metal_name}</td>,
                              <td key="stone" className="col-highlight">{result.stone_name}</td>,
                              <td key="size" className="col-highlight">{result.size_name}</td>,
                              <td key="stock" className="col-highlight text-center">
                                {result.total_stock ? result.total_stock : 0}
                              </td>,
                              <td key="so" className="col-highlight text-center danger-text">
                                {result.SalesOrder ? result.SalesOrder : 0}
                              </td>,
                              <td key="avail" className="col-highlight text-center">
                                {result.AvailableStock ? result.AvailableStock : 0}
                              </td>,
                              <td key="rp" className="col-highlight text-center">
                                {result.ReorderPoint_total ? result.ReorderPoint_total : 0}
                              </td>,
                              <td key="rq" className="col-highlight text-center status-green-text">
                                {result.ReorderQuantity_total ? result.ReorderQuantity_total : 0}
                              </td>,
                              <td key="qty" className="col-highlight">
                                <div className="col-input">
                                  <input
                                    className="col-width replenishqty"
                                    type="text"
                                    value={result.order_qty || 0}
                                    onChange={(e) => { changeorder_qty(e, result); setCuppyqty(e.target.value); setQty(e.target.value) }}
                                  />
                                </div>
                              </td>,
                            ];
                            return (
                              <tr key={rowProductId || index} className="createrepainshcuppy">
                                {(() => {
                                  let visibleIndex = -1;
                                  return newcolumndata?.map((col, colIdx) => {
                                    if (!col?.status) return null;
                                    visibleIndex += 1;
                                    const cell = cells[colIdx];
                                    if (!cell) return null;
                                    const stickyStyle = getStickyColStyle(visibleIndex, {
                                      rowIndex: index,
                                    });
                                    if (!stickyStyle) return cell;
                                    return React.cloneElement(cell, {
                                      className: `${cell.props.className || ""} sticky-col`.trim(),
                                      style: { ...(cell.props.style || {}), ...stickyStyle },
                                    });
                                  });
                                })()}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr>
                            {(() => {
                              let visibleIndex = -1;
                              return newcolumndata?.map((col, colIdx) => {
                                if (!col?.status) return null;
                                visibleIndex += 1;
                                const stickyStyle = getStickyColStyle(visibleIndex, { isHeader: true });
                                if (col.name === "check_box") {
                                  return (
                                    <td
                                      key={colIdx}
                                      className={`main-green-text left-0${stickyStyle ? " sticky-col" : ""}`}
                                      style={stickyStyle}
                                    />
                                  );
                                }
                                if (col.name === "Reorder Qty") {
                                  return (
                                    <td key={colIdx} className={stickyStyle ? "sticky-col" : undefined} style={stickyStyle}>
                                      {t("Total")}
                                    </td>
                                  );
                                }
                                if (col.name === "Qty") {
                                  return (
                                    <td key={colIdx} className={stickyStyle ? "sticky-col" : undefined} style={stickyStyle}>
                                      {Replenish_OrdersList
                                        ? Replenish_OrdersList.reduce(
                                          (interation, val) =>
                                            interation + parseInt(val?.order_qty ? val?.order_qty : 0),
                                          0
                                        )
                                        : 0}
                                    </td>
                                  );
                                }
                                return (
                                  <td
                                    key={colIdx}
                                    className={stickyStyle ? "sticky-col" : undefined}
                                    style={stickyStyle}
                                  />
                                );
                              });
                            })()}
                          </tr>
                        </tfoot>
                      </table>
                      {selecteditem.length > 0 ? (
                        <div className="replenish-selected-count">
                          {selecteditem.length} {t("item Selected")}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {showination ? "" :
                    Replenish_OrdersList.length > 0 && visibleOriginalPages.length > 0 ? (
                      <Pagination
                        total={visibleOriginalPages.length}
                        current={displayCurrentPage}
                        pagination={(crPage) => handlePaginations(crPage)}
                      />
                    ) : (
                      ""
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
       
        <SocialModal />
        <ProductModal data_product={getoneproduct} setOneproduct={setOneproduct} editproduct_submit={editproduct_submit} />
        {/* <NewFilterModal submitfiltter={stockTakelist} limit={0} custom_filter_enable={false} /> */}
      </div>
      {
        imageid ?
          <ProductImageSliderModal
            main_id={imageid}
          ></ProductImageSliderModal> : ''
      }
      <TableColumnModal
        tablehead={tbl_createreplenishHead}
        tblName={"tbl_creatreplenish"}
        setnewcolumndata={setnewcolumndata}
        calldatatable={getReplenishList}
      ></TableColumnModal>


      <ConfirmationModel statement={"Create Purchase Order"} handler_submit={po_create_submit}></ConfirmationModel>
      <LspNotesModal setForm_note={setForm_note}></LspNotesModal>
      <SkuModal onSubmit={(skuList) => skuBulkSearch(skuList)}></SkuModal>
      <Advancedfiltermodel></Advancedfiltermodel>
      <ImportFilesReplenishOrder setImportfile={setImportfile} readExcel={readExcel} importfile={importfile} />
      <PoPuSampleFormatModal sampleType="PO" />
      <div
        className="modal fade common-modal"
        id="replenishOrderExportFormatModal"
        tabIndex={-1}
        aria-labelledby="replenishOrderExportFormatModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <img
                className="common-modal-exclamation-img"
                src={base_url + "images/icons/exclamation-mark-green.png"}
                alt=""
              />
              <p className="modal-content-text mb-2">{t("Export format")}</p>
              <div className="d-flex justify-content-center gap-3 flex-wrap mb-3">
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="replenishOrderExportFormat"
                    value="xlsx"
                    checked={poExportFormat === "xlsx"}
                    onChange={() => setPoExportFormat("xlsx")}
                  />
                  {t("Excel (.xlsx)")}
                </label>
                <label className="d-flex align-items-center gap-1">
                  <input
                    type="radio"
                    name="replenishOrderExportFormat"
                    value="csv"
                    checked={poExportFormat === "csv"}
                    onChange={() => setPoExportFormat("csv")}
                  />
                  {t("CSV")}
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn modal-content-yes"
                data-bs-dismiss="modal"
                onClick={() => poimport(poExportFormat)}
              >
                {t("Export")}
              </button>
            </div>
          </div>
        </div>
      </div>

    </React.Fragment>
  );
};
export default CreateReplenishOrder;
